import { memo, useMemo } from 'react';
import type { FC } from 'react';

import type { EditorialComponent } from '@vgtv/api-client/lib/component/editorial';

import { EditorialSingle } from '../../../Editorial/EditorialSingle/EditorialSingle';
import { EditorialMulti } from '../../../Editorial/EditorialMulti/EditorialMulti';
import { useComponentAssets } from '../../../utils/useComponentAssets';
import {
  getAssetMainImage,
  getAssetVerticalImage,
} from '../../../../shared/helpers/assetHelpers';
import { getImageSources } from '../../../../helpers/images';

type Props = {
  componentConfig: EditorialComponent;
  pageComponentIndex: number;
};

const AssetsEditorialBase: FC<Props> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { id, options, header, content, editor } = componentConfig;

  const useVerticalPosters = options && options.useVerticalPosters;

  // no SVP fetch since the assets are preset
  const assets = useComponentAssets(id);

  const editorImageUrl = editor.image;

  const mappedAssets = useMemo(
    () =>
      assets.map((asset) => ({
        ...asset,
        imageSources: getImageSources(
          useVerticalPosters
            ? {
                imageUrl: getAssetVerticalImage(asset),
                imagePreset: 'editorialSliderVertical',
                aspectRatio: 'vertical',
              }
            : {
                imageUrl: getAssetMainImage(asset),
                imagePreset: 'editorialSlider',
                aspectRatio: 'horizontal',
              }
        ),
      })),
    [assets, useVerticalPosters]
  );

  const [asset] = assets;
  const imageSources = useMemo(
    () =>
      getImageSources({
        imageUrl: getAssetMainImage(asset),
        imagePreset: 'editorialSingle',
      }),
    [asset]
  );

  if (options && options.hasSingleItem) {
    if (!asset) {
      return null;
    }

    return (
      <EditorialSingle
        asset={asset}
        header={header}
        content={content}
        editorImageUrl={editorImageUrl}
        imageSources={imageSources}
      />
    );
  }

  return (
    <EditorialMulti
      header={header}
      content={content}
      editorImageUrl={editorImageUrl}
      assets={mappedAssets}
      useVerticalPosters={useVerticalPosters}
      pageComponentIndex={pageComponentIndex}
    />
  );
};

export const AssetsEditorial = memo(AssetsEditorialBase);
